import { useQuery } from '@tanstack/react-query'
import { load as parseYaml } from 'js-yaml'
import { graphql } from '@/lib/api'
import { TEMPLATE_TREE } from '@/lib/github-queries'
import { appConfig, keys } from '@/lib/query'
import {
  BLANK_TEMPLATE,
  templateFromIssueForm,
  type IssueTemplate,
} from '@/components/new-issue/templates'

/**
 * The new-issue templates, parsed live from the repo's issue-form ymls —
 * the dialog can't drift from what intake.yml expects. GitHub orders
 * templates by filename; we do the same. Blank is appended locally.
 */
export function useIssueTemplates() {
  return useQuery({
    queryKey: keys.issueTemplates,
    staleTime: 60 * 60 * 1000,
    queryFn: async (): Promise<IssueTemplate[]> => {
      const res = await graphql<{
        repository: { object: { entries?: Array<{ name: string; type: string }> } | null }
      }>({ query: TEMPLATE_TREE, variables: { owner: appConfig.owner, name: appConfig.repo } })

      const files = (res.repository.object?.entries ?? [])
        .filter((e) => e.type === 'blob' && /\.ya?ml$/.test(e.name) && e.name !== 'config.yml')
        .map((e) => e.name)
        .sort((a, b) => a.localeCompare(b))

      const templates = await Promise.all(
        files.map(async (file) => {
          const r = await fetch(`/api/work-files/.github/ISSUE_TEMPLATE/${file}`)
          if (!r.ok) return null
          try {
            return templateFromIssueForm(file, parseYaml(await r.text()) as never)
          } catch {
            return null
          }
        }),
      )

      return [...templates.filter((t): t is IssueTemplate => t !== null), BLANK_TEMPLATE]
    },
  })
}
